import { toUnix } from 'app/shared/utilities/time';
import { TimeSeriesCollection } from 'time-series-collection';
import { AdjustableProcessor } from 'app/shared/collections/time/processor/AdjustableProcessor';
import { AdjustableInterpolatorWrapper } from 'app/shared/collections/time/interpolator/interpolator';

interface GroupedSampleOptions {
    primaryKeyProp: string;
    secondaryKeyProp: string;
    startDateProp: string;
}

export class GroupedSampleProcessor<T> implements AdjustableProcessor<T> {
    private readonly _options: GroupedSampleOptions;
    private readonly _interpolator: AdjustableInterpolatorWrapper;

    // a map of maps of time series collections
    private readonly samples: Map<number, Map<string, TimeSeriesCollection<T>>>;

    constructor(options: GroupedSampleOptions, interpolator: AdjustableInterpolatorWrapper) {
        this._options = options;

        this._interpolator = interpolator;

        this.samples = new Map();
    }

    add(items: Array<T>): Promise<void> {
        const stats = Object.values(items);
        stats.forEach((stat) => {
            const primaryKey = stat[this._options.primaryKeyProp];
            const secondaryKey = stat[this._options.secondaryKeyProp];
            const date = stat[this._options.startDateProp];
            const timestamp = toUnix(date);

            if (this.samples.has(primaryKey)) {
                const map = this.samples.get(primaryKey);
                if (map.has(secondaryKey)) {
                    const collection = map.get(secondaryKey);
                    collection.addSample(timestamp, stat);
                } else {
                    const collection = new TimeSeriesCollection(this._interpolator.interpolator);
                    collection.addSample(timestamp, stat);
                    map.set(secondaryKey, collection);
                }
            } else {
                const collection = new TimeSeriesCollection(this._interpolator.interpolator);
                collection.addSample(timestamp, stat);
                const map = new Map([[secondaryKey, collection]]);
                this.samples.set(primaryKey, map);
            }
        });

        return Promise.resolve(undefined);
    }

    clear(): void {
        this.samples.clear();
    }

    remove(before: number, after: number): Promise<void> {
        for (const map of this.samples.values()) {
            for (const col of map.values()) {
                col.removeOutsideTimeFrame(before, after, true);
            }
        }
        return Promise.resolve(undefined);
    }

    get(key: number, time: number): Array<T> {
        const map = this.samples.get(key);
        if (map) {
            const values: Array<T> = [];
            for (const col of map.values()) {
                const value = col.getValue(time);
                if (value) {
                    values.push(value);
                }
            }
            return values;
        }
        return [];
    }

    getAllForKey(key: number): Array<T> {
        const values = [];
        const map = this.samples.get(key);
        if (map) {
            for (const col of map.values()) {
                values.push(...(col as any)._state.datums);
            }
        }
        return values;
    }

    getAll(): Array<T> {
        const values = [];
        for (const key of this.samples.keys()) {
            values.push(...this.getAllForKey(key));
        }
        return values;
    }

    // used by DynamicStatManager to determine if we need to change the statHoldSeconds
    getStatHoldSeconds(): number {
        return this._interpolator.getStatHoldSeconds();
    }

    // used by DynamicStatManager to change the statHoldSeconds
    setStatHoldSeconds(statHoldSeconds: number): void {
        this._interpolator.setStatHoldSeconds(statHoldSeconds);
    }
}
